import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
    Mail,
    Key,
    Copy,
    Check,
    UserCheck,
    UserMinus,
    Loader2,
    RefreshCw,
    AlertTriangle,
    ShieldCheck,
    Shield,
    UserPlus,
} from "lucide-react";
import api from "../../lib/api";

/**
 * Iter-27.77 — Admin tab "Compte partenaire" : un mini onboarding pour donner à
 * une association ses identifiants Creator Dashboard sans toucher au code.
 *
 * Trois états possibles :
 *  • Aucun compte → input email + bouton "Activer le compte"
 *  • Compte actif → email affiché + boutons "Désactiver" et "Régénérer mdp"
 *  • Compte désactivé → bandeau + bouton "Réactiver"
 *
 * Le mot de passe temporaire est affiché UNE SEULE FOIS (jamais re-fetchable).
 */
export default function PartnerAccountTab({ assoId, assoName }) {
    const [loading, setLoading] = useState(true);
    const [account, setAccount] = useState(null); // { exists, email, is_active, ... } or { exists: false }
    const [email, setEmail] = useState("");
    const [tempPassword, setTempPassword] = useState(""); // shown once, never re-fetched
    const [busy, setBusy] = useState(""); // "create" | "toggle" | "reset" | ""
    const [copied, setCopied] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get(`/admin/associations/${assoId}/partner-account`);
            setAccount(r.data);
            if (!r.data?.exists) {
                // Pré-remplir un email par défaut basé sur le nom de l'asso
                const slug = (assoName || "")
                    .toLowerCase()
                    .normalize("NFD")
                    .replace(/[\u0300-\u036f]/g, "")
                    .replace(/[^a-z0-9]+/g, "-")
                    .replace(/(^-|-$)/g, "")
                    .slice(0, 24);
                setEmail(slug ? `${slug}@partner.shariti.fr` : "");
            }
        } catch (e) {
            toast.error("Impossible de charger le compte partenaire");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [assoId]);

    const handleCreate = async () => {
        const cleaned = email.trim().toLowerCase();
        if (!cleaned || !cleaned.includes("@")) {
            toast.error("Email invalide");
            return;
        }
        setBusy("create");
        try {
            const r = await api.post(`/admin/associations/${assoId}/partner-account`, {
                email: cleaned,
            });
            setAccount(r.data);
            setTempPassword(r.data.temp_password || "");
            toast.success("Compte partenaire activé");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Erreur lors de la création");
        } finally {
            setBusy("");
        }
    };

    const handleToggle = async (next) => {
        setBusy("toggle");
        try {
            const r = await api.patch(`/admin/associations/${assoId}/partner-account`, {
                is_active: next,
            });
            setAccount({ ...account, ...r.data });
            toast.success(next ? "Compte réactivé" : "Compte désactivé");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Erreur");
        } finally {
            setBusy("");
        }
    };

    const handleReset = async () => {
        setBusy("reset");
        try {
            const r = await api.post(
                `/admin/associations/${assoId}/partner-account/reset-password`,
            );
            setTempPassword(r.data.temp_password || "");
            toast.success("Nouveau mot de passe généré");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Erreur");
        } finally {
            setBusy("");
        }
    };

    const copyPwd = async () => {
        try {
            await navigator.clipboard.writeText(tempPassword);
            setCopied(true);
            toast.success("Copié");
            setTimeout(() => setCopied(false), 1800);
        } catch {
            toast.error("Impossible de copier");
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-16">
                <Loader2 className="w-5 h-5 animate-spin text-shariti-orange" />
            </div>
        );
    }

    return (
        <div className="space-y-5 max-w-2xl" data-testid="partner-account-tab">
            {/* Intro */}
            <div className="rounded-2xl p-4 bg-shariti-orange/5 border border-shariti-orange/20">
                <div className="flex items-start gap-3">
                    <Shield className="w-5 h-5 text-shariti-orange-light shrink-0 mt-0.5" />
                    <div>
                        <p className="text-sm font-extrabold text-white mb-1">
                            Accès Creator Dashboard
                        </p>
                        <p className="text-xs text-white/65 leading-relaxed">
                            Donne à cette association un accès dédié à son backoffice partenaire
                            (<code className="text-shariti-orange-light bg-white/5 px-1 rounded">/asso-admin</code>).
                            Elle pourra publier ses propres remerciements, histoires d'impact
                            et actualités, sans toucher au reste de la plateforme.
                        </p>
                    </div>
                </div>
            </div>

            {/* État du compte */}
            {!account?.exists ? (
                <NoAccountForm
                    email={email}
                    onEmailChange={setEmail}
                    onCreate={handleCreate}
                    busy={busy === "create"}
                />
            ) : (
                <ExistingAccount
                    account={account}
                    onToggle={handleToggle}
                    onReset={handleReset}
                    busy={busy}
                />
            )}

            {/* Mot de passe temporaire (affiché 1 seule fois après create OU reset) */}
            {tempPassword && (
                <TempPasswordCard
                    email={account?.email}
                    password={tempPassword}
                    copied={copied}
                    onCopy={copyPwd}
                    onDismiss={() => setTempPassword("")}
                />
            )}
        </div>
    );
}


function NoAccountForm({ email, onEmailChange, onCreate, busy }) {
    return (
        <div
            className="rounded-3xl p-5 bg-white/3 border border-white/10 space-y-4"
            data-testid="partner-account-create-form"
        >
            <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-white/30" />
                <p className="text-[11px] font-thin tracking-[0.25em] uppercase text-white/55">
                    Aucun compte
                </p>
            </div>
            <div>
                <label className="text-[10px] font-thin tracking-[0.2em] uppercase text-white/50 mb-1.5 block">
                    Email du compte partenaire
                </label>
                <div className="relative">
                    <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                    <input
                        type="email"
                        value={email}
                        onChange={(e) => onEmailChange(e.target.value)}
                        placeholder="contact@asso.fr"
                        data-testid="partner-account-email-input"
                        className="w-full bg-white/5 border border-white/10 rounded-2xl pl-10 pr-4 py-3 text-white text-sm outline-none focus:border-shariti-orange/50"
                    />
                </div>
                <p className="text-[10px] text-white/40 mt-1.5 leading-relaxed">
                    L'email sert d'identifiant. Tu transmettras ensuite le mot de passe
                    temporaire à ton contact NGO.
                </p>
            </div>
            <button
                onClick={onCreate}
                disabled={busy || !email.trim()}
                data-testid="partner-account-create-btn"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-shariti-orange hover:bg-shariti-orange/90 text-white font-extrabold px-5 py-3 rounded-2xl text-sm transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            >
                {busy ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                    <UserPlus className="w-4 h-4" />
                )}
                Activer le compte
            </button>
        </div>
    );
}


function ExistingAccount({ account, onToggle, onReset, busy }) {
    const active = account.is_active !== false;
    return (
        <div
            className={`rounded-3xl p-5 border space-y-4 ${
                active
                    ? "bg-emerald-500/5 border-emerald-500/20"
                    : "bg-white/3 border-white/10"
            }`}
            data-testid="partner-account-existing"
        >
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2.5">
                    {active ? (
                        <>
                            <ShieldCheck className="w-5 h-5 text-emerald-300" />
                            <div>
                                <p className="text-[10px] font-thin tracking-[0.25em] uppercase text-emerald-300/80 leading-none mb-0.5">
                                    Compte actif
                                </p>
                                <p className="text-sm font-extrabold text-white">
                                    {account.email}
                                </p>
                            </div>
                        </>
                    ) : (
                        <>
                            <UserMinus className="w-5 h-5 text-white/45" />
                            <div>
                                <p className="text-[10px] font-thin tracking-[0.25em] uppercase text-white/55 leading-none mb-0.5">
                                    Désactivé
                                </p>
                                <p className="text-sm font-extrabold text-white/65 line-through">
                                    {account.email}
                                </p>
                            </div>
                        </>
                    )}
                </div>
            </div>

            <div className="flex flex-wrap gap-2 pt-1">
                {active ? (
                    <>
                        <button
                            onClick={() => onToggle(false)}
                            disabled={busy !== ""}
                            data-testid="partner-account-disable-btn"
                            className="inline-flex items-center gap-2 bg-white/5 hover:bg-white/10 text-white/85 font-bold px-4 py-2.5 rounded-2xl text-xs transition-all disabled:opacity-40"
                        >
                            {busy === "toggle" ? (
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            ) : (
                                <UserMinus className="w-3.5 h-3.5" />
                            )}
                            Désactiver
                        </button>
                        <button
                            onClick={onReset}
                            disabled={busy !== ""}
                            data-testid="partner-account-reset-btn"
                            className="inline-flex items-center gap-2 bg-shariti-orange/15 hover:bg-shariti-orange/25 text-shariti-orange-light font-bold px-4 py-2.5 rounded-2xl text-xs transition-all disabled:opacity-40"
                        >
                            {busy === "reset" ? (
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            ) : (
                                <RefreshCw className="w-3.5 h-3.5" />
                            )}
                            Régénérer un mot de passe
                        </button>
                    </>
                ) : (
                    <button
                        onClick={() => onToggle(true)}
                        disabled={busy !== ""}
                        data-testid="partner-account-enable-btn"
                        className="inline-flex items-center gap-2 bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-200 font-bold px-4 py-2.5 rounded-2xl text-xs transition-all disabled:opacity-40"
                    >
                        {busy === "toggle" ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                            <UserCheck className="w-3.5 h-3.5" />
                        )}
                        Réactiver
                    </button>
                )}
            </div>
        </div>
    );
}


function TempPasswordCard({ email, password, copied, onCopy, onDismiss }) {
    return (
        <div
            className="rounded-3xl p-5 bg-shariti-yellow/8 border border-shariti-yellow/30 space-y-3"
            data-testid="partner-account-temp-password"
        >
            <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-shariti-yellow shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                    <p className="text-sm font-extrabold text-shariti-yellow mb-1">
                        Mot de passe temporaire — affiché 1 seule fois
                    </p>
                    <p className="text-xs text-white/65 leading-relaxed">
                        Copie ces identifiants et transmets-les à ton contact NGO via un canal
                        sécurisé. Une fois cette carte fermée, le mot de passe ne sera plus
                        récupérable (mais tu peux toujours en régénérer un nouveau).
                    </p>
                </div>
            </div>

            <div className="rounded-2xl p-3 bg-black/40 border border-white/10 space-y-2">
                <div className="flex items-center gap-2">
                    <Mail className="w-3.5 h-3.5 text-white/45 shrink-0" />
                    <code className="text-xs text-white/85 font-mono truncate">{email}</code>
                </div>
                <div className="flex items-center gap-2">
                    <Key className="w-3.5 h-3.5 text-shariti-yellow shrink-0" />
                    <code className="text-sm text-shariti-yellow font-mono flex-1 truncate select-all">
                        {password}
                    </code>
                    <button
                        onClick={onCopy}
                        data-testid="partner-account-copy-pwd"
                        className="inline-flex items-center gap-1 bg-white/8 hover:bg-white/15 text-white/85 font-bold px-2.5 py-1.5 rounded-lg text-[11px] transition-all"
                    >
                        {copied ? (
                            <>
                                <Check className="w-3 h-3 text-emerald-300" />
                                Copié
                            </>
                        ) : (
                            <>
                                <Copy className="w-3 h-3" />
                                Copier
                            </>
                        )}
                    </button>
                </div>
            </div>

            <div className="flex justify-end">
                <button
                    onClick={onDismiss}
                    data-testid="partner-account-dismiss-pwd"
                    className="text-[11px] font-bold text-white/55 hover:text-white px-3 py-1.5 rounded-xl hover:bg-white/5 transition-all"
                >
                    J'ai bien transmis le mot de passe
                </button>
            </div>
        </div>
    );
}
